import { useEffect, useRef, useState } from 'react';

// A re-fetch faster than this never reveals a skeleton — the current rows are
// held instead, so a quick sort/search/page change doesn't flash a placeholder.
export const SKELETON_DELAY_MS = 180;

export interface DeferredLoadingInput<T> {
  // The rows the caller currently has (typically empty mid-fetch).
  rows: T[];
  // The last SETTLED rows, held so a brief re-fetch can keep showing them.
  heldRows: T[];
  loading: boolean;
  // The delay has passed while still loading.
  delayElapsed: boolean;
  // Whether ANY request has settled yet — tracked separately from the row
  // count. A table that settled on zero rows has nothing to hold but HAS been
  // loaded once, and keying "is this the first load?" off `heldRows.length`
  // made a re-fetch from a "No matches" state flash the skeleton immediately
  // (Codex, PR #150).
  settled: boolean;
}

// The anti-flicker rule itself, extracted as a pure function so it can be
// tested in this repo's node-only vitest runner — the hook around it is just
// wiring. (The suite renders with renderToStaticMarkup, which never runs
// effects or timers, and no DOM environment is configured.)
//
// Show the skeleton only while loading AND either the wait has grown long
// enough to be worth acknowledging, or there is nothing settled to hold in the
// meantime. Loading without the skeleton keeps the held rows on screen.
export function deferredLoadingState<T>({
  rows,
  heldRows,
  loading,
  delayElapsed,
  settled,
}: DeferredLoadingInput<T>): { displayRows: T[]; showSkeleton: boolean } {
  const showSkeleton = loading && (delayElapsed || !settled);
  const displayRows = loading && !showSkeleton ? heldRows : rows;
  return { displayRows, showSkeleton };
}

// Anti-flicker loading for a data table. While `loading`, a quick re-fetch keeps
// the previous rows on screen and shows the skeleton only once the wait crosses
// the delay; the first load (nothing settled yet) shows the skeleton at once.
// Generic over the row type — both the bookings and owners tables use it.
//
// Returns the rows to render (`displayRows`) and whether the body should show
// the skeleton (`showSkeleton`). The caller keeps the table header mounted
// either way, so the header never reloads on a re-fetch.
export function useDeferredLoading<T>(
  rows: T[],
  loading: boolean,
  delayMs = SKELETON_DELAY_MS,
): { displayRows: T[]; showSkeleton: boolean } {
  // Remember the last settled rows so a brief re-fetch can keep showing them,
  // and whether anything has settled at all — an empty result still counts.
  const lastRowsRef = useRef<T[]>([]);
  const settledRef = useRef(false);
  useEffect(() => {
    if (!loading) {
      lastRowsRef.current = rows;
      settledRef.current = true;
    }
  }, [loading, rows]);

  const [delayElapsed, setDelayElapsed] = useState(false);
  useEffect(() => {
    if (!loading) {
      setDelayElapsed(false);
      return;
    }
    const timer = setTimeout(() => setDelayElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [loading, delayMs]);

  return deferredLoadingState({
    rows,
    heldRows: lastRowsRef.current,
    loading,
    delayElapsed,
    settled: settledRef.current,
  });
}
